import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ParentContainor from '../../components/base/ParentContainor';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { CountryCode, DEFAULT_COUNTRY_ISO, getCountryByIso, getNationalMaxLength } from '../../constants/CountryCode';
import { authService } from '../../services/authService';
import { MESSAGES_LABELS } from '../../utils/promptMessages';
import { isValidEmail } from '../../utils/validation';
import { logger } from '../../utils/logger';
import './Login.css';

const LOGIN_METHOD = {
  MOBILE: 'mobile',
  EMAIL: 'email',
};

const Login = () => {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState(LOGIN_METHOD.MOBILE);
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const selectedCountry = getCountryByIso(countryIso);
  const countryCode = selectedCountry.code;
  const nationalMaxLength = getNationalMaxLength(countryCode);

  useEffect(() => {
    inputRef.current?.focus();
  }, [loginMethod]);

  const handleChangeMobile = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, nationalMaxLength);
    setMobileNumber(value);
    setError(null);
  };

  const handleCountryChange = (e) => {
    const nextIso = e.target.value;
    const nextCountry = getCountryByIso(nextIso);
    const nextMax = getNationalMaxLength(nextCountry.code);
    setCountryIso(nextIso);
    setMobileNumber((prev) => prev.slice(0, nextMax));
    setError(null);
  };

  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
    setError(null);
  };

  const switchMethod = (method) => {
    if (method === loginMethod) return;
    setLoginMethod(method);
    setError(null);
    if (method === LOGIN_METHOD.MOBILE) {
      setEmail('');
    } else {
      setMobileNumber('');
    }
  };

  const handleGetOTP = async (e) => {
    e?.preventDefault();
    if (!agreedToTerms) {
      setError(MESSAGES_LABELS.error.TERMS_REQUIRED);
      return;
    }

    if (loginMethod === LOGIN_METHOD.MOBILE) {
      if (!mobileNumber) {
        setError(MESSAGES_LABELS.error.EMAIL_OR_MOBILE_REQUIRED);
        return;
      }
      if (mobileNumber.length < 6) {
        setError(MESSAGES_LABELS.error.MOBILE_NUMBER_INVALID);
        return;
      }
    } else {
      if (!email) {
        setError(MESSAGES_LABELS.error.EMAIL_OR_MOBILE_REQUIRED);
        return;
      }
      if (!isValidEmail(email)) {
        setError(MESSAGES_LABELS.error.EMAIL_INVALID);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      if (loginMethod === LOGIN_METHOD.MOBILE) {
        const fullPhoneNumber = `${countryCode}${mobileNumber}`;
        await authService.signIn(fullPhoneNumber);
        logger.log('Navigating to : ', ROUTE_ENUM.AUTH_OTP.routePath);
        navigate(ROUTE_ENUM.AUTH_OTP.routePath, {
          state: {
            phoneNumber: fullPhoneNumber,
            countryCode,
            isSignUp: false,
          },
        });
      } else {
        const trimmedEmail = email.trim();
        await authService.signInEmail(trimmedEmail);
        navigate(ROUTE_ENUM.AUTH_OTP.routePath, {
          state: {
            email: trimmedEmail,
            isSignUp: false,
          },
        });
      }
    } catch (err) {
      if (err?.message === 'CreateAuthChallenge failed with error Failed to send OTP.') {
        setError(MESSAGES_LABELS.error.AUTH_ERROR_NOT_REGISTERED);
        return;
      }
      setError(err.message || MESSAGES_LABELS.error.FAILED_TO_SEND_OTP);
    } finally {
      setIsLoading(false);
    }
  };

  const isMobile = loginMethod === LOGIN_METHOD.MOBILE;

  return (
    <ParentContainor
      route={ROUTE_ENUM.AUTH_LOGIN}
      showCopyRightText
      disablePadding
      isShowHeader={false}
    >
      <div className="rap-auth">
        <div className="rap-auth__card">
          <div className="rap-auth__brand">
            <img
              className="rap-auth__logo"
              src={IMAGE_CONSTANTS.V2_REVAMPED_RAPCHIK_LOGO}
              alt="Rapchik"
              width={187}
              height={166}
            />
            <h1 className="rap-auth__headline">
              <span className="rap-auth__headline-regular">{TEXT_LABELS.AUTH_LOGIN_HEADLINE_KHELO}</span>
              {` ${TEXT_LABELS.AUTH_LOGIN_HEADLINE_DIL_SE} `}
              <span className="rap-auth__headline-regular">{TEXT_LABELS.AUTH_LOGIN_HEADLINE_JEETO}</span>
              {` ${TEXT_LABELS.AUTH_LOGIN_HEADLINE_DIMAAG_SE}`}
            </h1>
          </div>

          <img
            className="rap-auth__divider"
            src={IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_DIVIDER}
            alt=""
          />

          <p className="rap-auth__prompt">{TEXT_LABELS.AUTH_LOGIN_CONTINUE}</p>

          <form className="rap-auth__form" onSubmit={handleGetOTP}>
            <div className="rap-auth__toggle" role="tablist" aria-label={TEXT_LABELS.AUTH_LOGIN_CONTINUE}>
              <button
                type="button"
                role="tab"
                aria-selected={isMobile}
                className={`rap-auth__toggle-btn${isMobile ? ' is-active' : ''}`}
                onClick={() => switchMethod(LOGIN_METHOD.MOBILE)}
              >
                {TEXT_LABELS.AUTH_LOGIN_TAB_MOBILE}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isMobile}
                className={`rap-auth__toggle-btn${!isMobile ? ' is-active' : ''}`}
                onClick={() => switchMethod(LOGIN_METHOD.EMAIL)}
              >
                {TEXT_LABELS.AUTH_LOGIN_TAB_EMAIL}
              </button>
            </div>

            <div className="rap-auth__field">
              {isMobile ? (
                <>
                  <span className="rap-auth__cc">
                    <span className="rap-auth__cc-value">{countryCode}</span>
                    <span className="rap-auth__cc-chevron">
                      <img
                        src={IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_CHEVRON}
                        alt=""
                        width={10}
                        height={16}
                      />
                    </span>
                    <select
                      className="rap-auth__cc-select"
                      value={countryIso}
                      aria-label={TEXT_LABELS.AUTH_COUNTRY_CODE_ARIA}
                      onChange={handleCountryChange}
                    >
                      {CountryCode.map((cc) => (
                        <option key={cc.iso} value={cc.iso}>
                          {`${cc.flag} ${cc.label} (${cc.code})`}
                        </option>
                      ))}
                    </select>
                  </span>
                  <span className="rap-auth__field-rule" aria-hidden="true">
                    <img
                      src={IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_FIELD_RULE}
                      alt=""
                      width={32}
                      height={1}
                    />
                  </span>
                </>
              ) : null}

              <input
                ref={inputRef}
                className="rap-auth__input"
                type={isMobile ? 'tel' : 'email'}
                inputMode={isMobile ? 'numeric' : 'email'}
                autoComplete={isMobile ? 'tel' : 'email'}
                aria-label={
                  isMobile
                    ? TEXT_LABELS.AUTH_INPUT_PHONE_PLACEHOLDER
                    : TEXT_LABELS.AUTH_INPUT_EMAIL_PLACEHOLDER
                }
                placeholder={
                  isMobile
                    ? TEXT_LABELS.AUTH_INPUT_PHONE_PLACEHOLDER
                    : TEXT_LABELS.AUTH_INPUT_EMAIL_PLACEHOLDER
                }
                value={isMobile ? mobileNumber : email}
                maxLength={isMobile ? nationalMaxLength : 50}
                onChange={isMobile ? handleChangeMobile : handleChangeEmail}
              />
            </div>

            <div className="rap-auth__consent">
              <button
                type="button"
                className="rap-auth__check"
                aria-pressed={agreedToTerms}
                aria-label={TEXT_LABELS.AUTH_TERMS_ARIA}
                onClick={() => {
                  setAgreedToTerms((prev) => !prev);
                  setError(null);
                }}
              >
                <img
                  src={
                    agreedToTerms
                      ? IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_CHECKBOX_CHECKED
                      : IMAGE_CONSTANTS.V2_REVAMPED_LOGIN_CHECKBOX
                  }
                  alt=""
                  width={20}
                  height={20}
                />
              </button>
              <p className="rap-auth__consent-copy">
                {TEXT_LABELS.AUTH_TERMS_PREFIX}
                <a
                  className="rap-auth__consent-link"
                  href={TEXT_LABELS.AUTH_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {TEXT_LABELS.AUTH_TERMS_LINK}
                </a>
                {TEXT_LABELS.AUTH_TERMS_AND}
                <a
                  className="rap-auth__consent-link"
                  href={TEXT_LABELS.AUTH_PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {TEXT_LABELS.AUTH_PRIVACY_LINK}
                </a>
              </p>
            </div>

            {error ? <div className="rap-auth__error">{error}</div> : null}

            <button
              type="submit"
              className="rap-auth__submit"
              disabled={isLoading || !agreedToTerms}
            >
              <span className="rap-auth__submit-face">
                {isLoading ? TEXT_LABELS.AUTH_ACTION_BUTTON_SEND_OTP : TEXT_LABELS.AUTH_ACTION_BUTTON_LOGIN}
              </span>
            </button>
          </form>
        </div>
      </div>
    </ParentContainor>
  );
};

export default Login;
